// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Camera from "@insite/mobius/Icons/Camera";
import FileText from "@insite/mobius/Icons/FileText";
import Rss from "@insite/mobius/Icons/Rss";
import Twitter from "@insite/mobius/Icons/Twitter";
import User from "@insite/mobius/Icons/User";
import Video from "@insite/mobius/Icons/Video";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Facebook from "@synd/XDL/Icons/FooterSocialFacebook";
import FooterSocialInstagram from "@synd/XDL/Icons/FooterSocialInstagram";
import Linkedin from "@synd/XDL/Icons/FooterSocialLinkedIn";
import Pinterest from "@synd/XDL/Icons/FooterSocialPinterest";
import Youtube from "@synd/XDL/Icons/FooterSocialYouTube";
import IconPhone from "@synd/XDL/Icons/HeaderMinimalPhone";
import Mail from "@synd/XDL/Icons/LinkEmail";
import { color } from "@synd/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import styled, { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    visibilityState = "visibilityState",
    iconSize = "iconSize",
    iconColor = "iconColor",
    backgroundColor = "backgroundColor",
    linksPerRow = "linksPerRow",
    customCSS = "customCSS",
    // XNG-change
    labelColor = "labelColor",
    linkTitleColor = "linkTitleColor",
    heading = "heading",
    divided = "divided",
    label = "label",
    linksAlignment = "linksAlignment",
}

const enum icons {
    instagram = "Instagram",
    facebook = "Facebook",
    youtube = "Youtube",
    twitter = "Twitter",
    xTwitter = "X",
    rss = "Rss",
    user = "User",
    linkedin = "Linkedin",
    camera = "Camera",
    video = "Video",
    notebook = "Notebook",
    // XNG-Changes
    phone = "Phone",
    pinterest = "Pinterest",
    mail = "mail",
}

const enum visibilityState {
    both = "both",
    label = "label",
    icon = "icon",
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        // XNG-Change
        label: string;
    };
}

export interface LinkListStyles {
    socialLinkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    // XNG-Change:
    titleText?: TypographyProps;
    wrapper?: InjectableCss;
    linkLabel?: TypographyProps;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "right";
        [fields.visibilityState]: visibilityState;
        [fields.title]: string;
        [fields.iconSize]: number;
        [fields.linksPerRow]: number;
        [fields.iconColor]: string;
        [fields.backgroundColor]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
        // XNG-Change
        [fields.labelColor]: string;
        [fields.linkTitleColor]: string;
        [fields.heading]: string;
        [fields.divided]: string;
        [fields.linksAlignment]: string;
    };
    // XNG-change
    expandedStyles?: LinkListStyles;
}

export const linkListStyles: LinkListStyles = {
    socialLinkListWrapper: {
        css: css`
            width: 100%;
            padding: 10px 0;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
            margin-right: 5px;
            flex-shrink: 0;
        `,
    },
    link: {
        typographyProps: {
            color: "text.main",
        },
    },
};
// XNG-Change
const Divider = styled("span")`
    margin: 0 4px;
    width: 1px;
    height: 17px;
    background-color: ${color("common.ui1")};
`;

export const socialLinksStyles = linkListStyles;

export const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.youtube:
            return Youtube;
        case icons.twitter:
            return Twitter;
        case icons.facebook:
            return Facebook;
        case icons.pinterest:
            return Pinterest;
        case icons.instagram:
            return FooterSocialInstagram;
        case icons.linkedin:
            return Linkedin;
        case icons.notebook:
            return FileText;
        case icons.rss:
            return Rss;
        case icons.user:
            return User;
        case icons.video:
            return Video;
        case icons.camera:
            return Camera;
        // XNG-Changes
        case icons.phone:
            return IconPhone;
        case icons.mail:
            return Mail;
        default:
            break;
    }

    return "";
};

export const SocialLinks: FC<OwnProps> = ({ fields, expandedStyles }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);
    // XNG-Change
    const styles = useMergeStyles("socialLinks", linkListStyles, expandedStyles);

    // XNG-Changes
    const customCssWrapper = {
        css: css`
            ${fields.backgroundColor && `background-color: ${fields.backgroundColor};`}
            ${fields.customCSS};
            ${styles.wrapper?.css}
        `,
    };
    const socialLinkListStyle = css`
        ${fields.direction === "horizontal" ? "flex-direction: row; gap: 12px;" : "flex-direction: column; gap: 12px;"}
        // XNG-Change
        
         ${fields.linksAlignment === "left"
            ? fields.direction === "horizontal"
                ? "justify-content: start;"
                : "align-items: flex-start;"
            : fields.direction === "horizontal"
              ? "justify-content: end;"
              : "align-items: flex-end;"} 
        ${styles.socialLinkListWrapper?.css}
    `;

    const linkWrapperStyle = css`
        ${fields.direction === "horizontal" && `flex-basis: calc(${100 / fields.linksPerRow}% - 20px);`}
        ${fields.direction === "horizontal" && fields.linksPerRow > links.length ? "flex-grow: 1;" : "flex-grow: 0;"}
        ${styles.linkWrapper?.css}
    `;

    const showIcon = fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.icon;
    const showLabel =
        fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.label;

    return (
        <StyledWrapper {...customCssWrapper}>
            {/* XNG-Change: */}
            {fields.heading && <Typography {...styles.titleText}>{fields.heading}</Typography>}
            <StyledWrapper className="social-links" css={socialLinkListStyle}>
                {links.map((link, index: number) => (
                    <React.Fragment
                        // className="link-wrapper"
                        // css={linkWrapperStyle}

                        key={index}
                    >
                        {/* Xng-Change */}
                        {index > 0 && fields.divided && <Divider />}
                        {/* Xng-Change add label */}
                        <StyledWrapper {...styles.linkWrapper}>
                            {!!fields.links[index]?.fields?.label && (
                                <Typography
                                    {...styles.linkLabel}
                                    className="social-link-label"
                                    color={fields.labelColor}
                                >
                                    {fields.links[index].fields.label}
                                </Typography>
                            )}
                            {link.url && (
                                <Link
                                    // XNG-Change
                                    className={`social-link ${fields.links[index]?.fields?.label ? "with-label" : ""}`}
                                    {...styles.link}
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    icon={{
                                        iconProps: {
                                            size: fields.iconSize,
                                            color: fields.iconColor,
                                            src: showIcon ? GetIcon(fields.links[index].fields.icon) : undefined,
                                            title: fields.links[index].fields.title,
                                            css: css`
                                                margin-right: 0;
                                            `,
                                        },
                                        position: fields.alignment,
                                    }}
                                    alt="social-link"
                                    aria-label="social-link"
                                    color={fields.linkTitleColor}
                                >
                                    {showLabel && fields.links[index].fields.title}
                                </Link>
                            )}
                        </StyledWrapper>
                    </React.Fragment>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.social-links{
}

.link-wrapper{
}

.social-link{
}`;

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "thumbs-up",
    isSystem: true,
    fieldDefinitions: [
        {
            name: fields.heading,
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tab: contentTab,
        },
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Instagram", value: icons.instagram },
                        { displayName: "Youtube", value: icons.youtube },
                        { displayName: "Facebook", value: icons.facebook },
                        { displayName: "Twitter", value: icons.twitter },
                        { displayName: "X", value: icons.xTwitter },
                        { displayName: "Rss", value: icons.rss },
                        { displayName: "User", value: icons.user },
                        { displayName: "Linkedin", value: icons.linkedin },
                        { displayName: "Camera", value: icons.camera },
                        { displayName: "Video", value: icons.video },
                        { displayName: "Notebook", value: icons.notebook },
                        // XNG-Changes
                        { displayName: "Phone", value: icons.phone },
                        { displayName: "Pinterest", value: icons.pinterest },
                    ],
                    defaultValue: "",
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
                // XNG-Change
                {
                    name: fields.label,
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
            ],
        },
        {
            name: fields.direction,
            displayName: "Display",
            editorTemplate: "RadioButtonsField",
            defaultValue: "horizontal",
            horizontal: true,
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 1,
            options: [
                {
                    displayName: "Column",
                    value: "vertical",
                },
                {
                    displayName: "Row",
                    value: "horizontal",
                },
            ],
        },
        {
            name: fields.alignment,
            displayName: "Text Position",
            editorTemplate: "RadioButtonsField",
            defaultValue: "left",
            horizontal: true,
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 2,
            options: [
                {
                    displayName: "Left of icon",
                    value: "left",
                },
                {
                    displayName: "Right of icon",
                    value: "right",
                },
            ],
        },
        // XNG-Change
        {
            name: fields.linksAlignment,
            displayName: "Links Alignment",
            editorTemplate: "DropDownField",
            options: [
                {
                    value: "left",
                    displayName: "Left",
                },
                {
                    value: "right",
                    displayName: "Right",
                },
            ],
            defaultValue: "left",
            fieldType: "General",
            hideEmptyOption: true,
            tab: settingsTab,
        },
        {
            name: fields.visibilityState,
            displayName: "Visibility",
            editorTemplate: "RadioButtonsField",
            tab: settingsTab,
            options: [
                {
                    displayName: "Show Both",
                    value: visibilityState.both,
                },
                {
                    displayName: "Show Label Only",
                    value: visibilityState.label,
                },
                {
                    displayName: "Show Icon Only",
                    value: visibilityState.icon,
                },
            ],
            defaultValue: visibilityState.both,
            fieldType: "General",
            sortOrder: 4,
        },
        {
            name: fields.iconSize,
            displayName: "Icon Size",
            editorTemplate: "IntegerField",
            defaultValue: 30,
            min: 1,
            fieldType: "General",
            sortOrder: 3,
            tab: settingsTab,
        },
        {
            name: fields.linksPerRow,
            displayName: "Icons Per Row",
            fieldType: "General",
            editorTemplate: "DropDownField",
            options: [
                { value: 1 },
                { value: 2 },
                { value: 3 },
                { value: 4 },
                { value: 5 },
                { value: 6 },
                { value: 7 },
                { value: 8 },
            ],
            sortOrder: 3,
            defaultValue: 8,
            isVisible: (item?: HasFields) => {
                return item?.fields.direction === "horizontal";
            },
            tab: settingsTab,
        },
        {
            name: fields.iconColor,
            displayName: "Icon Color",
            editorTemplate: "ColorPickerField",
            popoverLocation: "top",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 5,
            tab: settingsTab,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            popoverLocation: "top",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 6,
            tab: settingsTab,
        },
        // Xng-Change
        {
            name: fields.divided,
            displayName: "Has Divider",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: settingsTab,
        },
        // XNG-change
        {
            name: fields.labelColor,
            displayName: "Label Color",
            editorTemplate: "ColorPickerField",
            popoverLocation: "top",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 6,
            tab: settingsTab,
        },
        {
            name: fields.linkTitleColor,
            displayName: "Link Title Color",
            editorTemplate: "ColorPickerField",
            popoverLocation: "top",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 6,
            tab: settingsTab,
        },
        {
            name: fields.customCSS,
            displayName: "Custom CSS",
            editorTemplate: "CodeField",
            fieldType: "General",
            tab: advancedTab,
            defaultValue: defaultCustomCss,
            isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            validate: value => {
                if (value === undefined || value === null) {
                    return "";
                }

                const result = cssLinter.parse(value, { silent: true });
                if (result?.stylesheet?.parsingErrors) {
                    // the error output at this time only has room for one line so we just show the first error
                    return result.stylesheet.parsingErrors.length <= 0
                        ? ""
                        : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                }

                return "Unable to parse Css";
            },
            options: {
                mode: "css",
                lint: true,
                autoRefresh: true,
            },
        },
    ],
};

const widgetModule: WidgetModule = {
    component: SocialLinks,
    definition,
};

export default widgetModule;
